"use client";

import { useEffect, useRef, useState } from "react";
import type { UserCategory } from "@/lib/contact";
import type { Locale } from "@/lib/i18n";
import {
  CAPTCHA_REQUIRED_MESSAGE,
  getCaptchaErrorMessage,
} from "@/lib/turnstile-client";
import TurnstileWidget from "./TurnstileWidget";
import { isTurnstileWidgetEnabled } from "@/lib/turnstile-widget";

type Props = {
  defaultName?: string;
  defaultEmail?: string;
  defaultCategory?: UserCategory;
  defaultResultSlug?: string;
  defaultPageUrl?: string;
  isAuthenticated?: boolean;
  locale?: Locale;
};

const CATEGORY_OPTIONS: Record<
  Locale,
  { value: UserCategory; label: string }[]
> = {
  ko: [
    { value: "bug", label: "오류 · 버그 제보" },
    { value: "result", label: "결과 · 테스트 문의" },
    { value: "account", label: "계정 · 로그인 문제" },
    { value: "suggestion", label: "서비스 개선 제안" },
    { value: "etc", label: "기타" },
  ],
  en: [
    { value: "bug", label: "Bug report" },
    { value: "result", label: "Quiz or result question" },
    { value: "account", label: "Account or login issue" },
    { value: "suggestion", label: "Product suggestion" },
    { value: "etc", label: "Other" },
  ],
};

const MAX_MESSAGE = 2000;

function isUserCategory(v: unknown): v is UserCategory {
  return (
    v === "bug" ||
    v === "result" ||
    v === "account" ||
    v === "suggestion" ||
    v === "etc"
  );
}

export default function UserSupportForm({
  defaultName = "",
  defaultEmail = "",
  defaultCategory = "bug",
  defaultResultSlug = "",
  defaultPageUrl = "",
  isAuthenticated = false,
  locale = "ko",
}: Props) {
  const initialCategory: UserCategory = isUserCategory(defaultCategory)
    ? defaultCategory
    : "bug";
  const [category, setCategory] = useState<UserCategory>(initialCategory);
  const [name, setName] = useState(defaultName);
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState("");
  const [pageUrl, setPageUrl] = useState(defaultPageUrl);
  const [website, setWebsite] = useState("");
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const turnstileTokenRef = useRef<string | null>(null);
  const [turnstileResetSignal, setTurnstileResetSignal] = useState(0);
  const mountedAtRef = useRef<number>(0);
  const relatedSlug = defaultResultSlug.trim();

  useEffect(() => {
    mountedAtRef.current = Date.now();
  }, []);

  const remaining = MAX_MESSAGE - message.length;
  const copy =
    locale === "ko"
      ? {
          required: "필수 항목을 모두 입력해 주세요.",
          maxMessage: `문의 내용은 최대 ${MAX_MESSAGE.toLocaleString()}자입니다.`,
          captchaRequired: CAPTCHA_REQUIRED_MESSAGE,
          captchaFailed: "보안 확인에 실패했어요. 다시 시도해 주세요.",
          rateLimited: "요청이 너무 많습니다. 잠시 후 다시 시도해 주세요.",
          forbidden: "현재 계정으로는 문의를 보낼 수 없습니다.",
          submitFailed:
            "문의 전송에 실패했어요. 입력 내용을 확인한 뒤 다시 시도해 주세요.",
          networkFailed: "네트워크 오류가 발생했어요. 잠시 후 다시 시도해 주세요.",
          submittedTitle: "문의가 접수되었어요",
          submittedBody:
            "보내주신 메일 주소로 평일 기준 3일 이내에 회신드릴게요.",
          authNotice:
            "로그인한 계정으로 제출됩니다. 이름·이메일은 수정할 수 있어요.",
          relatedPrefix: "결과 페이지",
          relatedSuffix: "에서 연결된 문의입니다.",
          category: "분류",
          name: "이름 또는 닉네임",
          email: "회신 받을 이메일",
          message: "문의 내용",
          bugPlaceholder:
            "어떤 화면에서 어떤 동작을 할 때 문제가 생겼는지, 가능하면 사용 기기도 함께 알려 주세요.",
          messagePlaceholder:
            "자세한 내용을 적어 주시면 더 빨리 답변드릴 수 있어요.",
          pageUrl: "문제가 생긴 페이지 주소 (선택)",
          pageUrlPlaceholder: "예) https://modu-test.com/tests/mbti",
          remaining: "남음",
          privacy:
            "제출된 정보는 문의 응답 목적으로만 사용되며, 처리 완료 후 30일이 지나면 자동으로 파기됩니다. IP 주소 등 접속 기록은 24시간 후 삭제됩니다.",
          pending: "전송 중...",
          submit: "문의 보내기",
        }
      : {
          required: "Please fill in all required fields.",
          maxMessage: `Message is limited to ${MAX_MESSAGE.toLocaleString("en-US")} characters.`,
          captchaRequired: "Please complete the security check.",
          captchaFailed: "Security verification failed. Please try again.",
          rateLimited: "Too many requests. Please try again later.",
          forbidden: "This account cannot send support requests.",
          submitFailed:
            "We could not send your request. Check the fields and try again.",
          networkFailed: "A network error occurred. Please try again later.",
          submittedTitle: "Support request received",
          submittedBody:
            "We will reply to the email address you provided within 3 business days.",
          authNotice:
            "This will be submitted with your signed-in account. You can edit the name and email.",
          relatedPrefix: "This request is connected to result page",
          relatedSuffix: ".",
          category: "Category",
          name: "Name or nickname",
          email: "Reply email",
          message: "Message",
          bugPlaceholder:
            "Tell us which page and action caused the issue. Include your device if possible.",
          messagePlaceholder:
            "Add details so we can respond faster.",
          pageUrl: "Problem page URL (optional)",
          pageUrlPlaceholder: "e.g. https://modu-test.com/en/tests/mbti",
          remaining: "left",
          privacy:
            "Submitted information is used only to respond to support requests and is deleted 30 days after processing. Access logs such as IP addresses are deleted after 24 hours.",
          pending: "Sending...",
          submit: "Send request",
        };

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) {
      setError(copy.required);
      return;
    }
    if (message.length > MAX_MESSAGE) {
      setError(copy.maxMessage);
      return;
    }
    const turnstileToken = turnstileTokenRef.current;
    if (isTurnstileWidgetEnabled() && !turnstileToken) {
      setError(copy.captchaRequired);
      return;
    }
    setError(null);
    setIsPending(true);
    try {
      const res = await fetch("/api/support", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          category,
          name,
          email,
          message,
          website,
          mountedAt: mountedAtRef.current,
          relatedResultSlug: relatedSlug || null,
          pageUrl: pageUrl || null,
          turnstileToken,
        }),
      });
      if (res.ok) {
        setSubmitted(true);
        return;
      }
      const captchaError = await getCaptchaErrorMessage(res);
      if (captchaError) {
        setError(locale === "ko" ? captchaError : copy.captchaFailed);
        return;
      }
      if (res.status === 429) {
        setError(copy.rateLimited);
        return;
      }
      if (res.status === 403) {
        setError(copy.forbidden);
        return;
      }
      setError(copy.submitFailed);
    } catch {
      setError(copy.networkFailed);
    } finally {
      setIsPending(false);
      turnstileTokenRef.current = null;
      setTurnstileResetSignal((value) => value + 1);
    }
  }

  if (submitted) {
    return (
      <section className="mx-auto flex w-full max-w-lg flex-col items-center gap-3 rounded-3xl border border-emerald-200 bg-emerald-50 p-6 text-center">
        <h2 className="font-display text-xl text-emerald-800">
          {copy.submittedTitle}
        </h2>
        <p className="text-sm leading-6 text-emerald-700">
          {copy.submittedBody}
        </p>
      </section>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mx-auto flex w-full max-w-lg flex-col gap-4 rounded-3xl bg-white/80 p-6 shadow-pop"
      noValidate
    >
      {isAuthenticated && (
        <p className="rounded-xl bg-brand-50 px-3 py-2 text-xs text-brand-800">
          {copy.authNotice}
        </p>
      )}
      {relatedSlug && (
        <p className="rounded-xl bg-slate-100 px-3 py-2 text-xs text-slate-600">
          {copy.relatedPrefix}{" "}
          <code className="rounded bg-white px-1 text-[11px]">
            /r/{relatedSlug}
          </code>{" "}
          {copy.relatedSuffix}
        </p>
      )}

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.category}
        <select
          value={category}
          onChange={(e) =>
            setCategory(
              isUserCategory(e.target.value) ? e.target.value : "bug"
            )
          }
          disabled={isPending}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        >
          {CATEGORY_OPTIONS[locale].map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.name}
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={isPending}
          maxLength={50}
          required
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.email}
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isPending}
          maxLength={120}
          required
          placeholder="example@modu-test.com"
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.message}
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isPending}
          rows={6}
          maxLength={MAX_MESSAGE}
          required
          placeholder={
            category === "bug"
              ? copy.bugPlaceholder
              : copy.messagePlaceholder
          }
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
        <span className="text-right text-xs text-slate-400">
          {message.length.toLocaleString()} / {MAX_MESSAGE.toLocaleString()}
          <span className="ml-1 text-slate-300">
            ({remaining.toLocaleString()} {copy.remaining})
          </span>
        </span>
      </label>

      <label className="flex flex-col gap-1 text-sm text-slate-700">
        {copy.pageUrl}
        <input
          type="text"
          value={pageUrl}
          onChange={(e) => setPageUrl(e.target.value)}
          disabled={isPending}
          maxLength={500}
          placeholder={copy.pageUrlPlaceholder}
          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
        />
      </label>

      <p className="text-xs text-slate-500">
        {copy.privacy}
      </p>

      <div aria-hidden className="absolute -left-[9999px]">
        <label>
          Website
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </label>
      </div>

      {error && (
        <p role="alert" className="rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </p>
      )}

      <TurnstileWidget
        action="support"
        onTokenChange={(token) => {
          turnstileTokenRef.current = token;
        }}
        resetSignal={turnstileResetSignal}
      />

      <button
        type="submit"
        disabled={isPending}
        className="rounded-xl bg-brand-gradient py-2 text-sm font-medium text-white shadow-pop disabled:opacity-60"
      >
        {isPending ? copy.pending : copy.submit}
      </button>
    </form>
  );
}
